/* @jsx h */
import {
  defineComponent,
  inject,
  onMounted,
  provide,
  ref,
  h,
  type Ref,
} from "vue";

interface LocalizationContextType {
  t: (key: string, params?: (string | number)[]) => string;
  locales: { id: string; name: string; translations: Record<string, string> }[];
  locale: Ref<string>;
  translations: Ref<Record<string, string>>;
  setLocale: (lang: string) => void;
}

export const supportedLanguages = [
  { name: "Deutsch", id: "de" },
  { name: "Français", id: "fr" },
  { name: "English", id: "en" },
  { name: "Italiano", id: "it" },
  { name: "ქართული", id: "ka" },
];

const LocalizationContextKey = Symbol("LocalizationContext");

// Shown when a language's dictionary fails to load. Every app that mounts the
// provider carries this row in its translations.csv (web, customer,
// marketing).
export const LOAD_FAILED_KEY = "uikit.localization.load_failed";

export type LocalizationProviderProps = {
  files: { [lang: string]: string }; // e.g. { en: "/en.json", fr: "/fr.json" }
  initialLocale?: string;
  /**
   * Dictionary for `initialLocale`, already in memory. When set, the provider
   * is loaded synchronously in setup() — no fetch, no onMounted — so it
   * renders its slot on the first pass. This is what lets a server render
   * (and the client hydration that must match it) work at all: onMounted
   * never runs on the server. Consumers that still fetch (web, customer)
   * simply don't pass it.
   */
  initialTranslations?: Record<string, string>;
};

export const LocalizationProvider = defineComponent<LocalizationProviderProps>({
  props: ["files", "initialLocale", "initialTranslations"],
  setup(props, { slots }) {
    const loaded = ref(false);
    const locale = ref("");
    const translations = ref<Record<string, string>>({});
    const locales = ref<LocalizationContextType["locales"]>([]);

    if (props.initialLocale && props.initialTranslations) {
      translations.value = props.initialTranslations;
      locale.value = props.initialLocale;
      locales.value.push({
        id: props.initialLocale,
        name: props.initialLocale.toUpperCase(),
        translations: props.initialTranslations,
      });
      loaded.value = true;
    }

    // Load translation file dynamically
    const loadLocale = async (lang: string) => {
      const url = props.files[lang];
      if (!url) return;

      try {
        const res = await fetch(url);
        const data: Record<string, string> = await res.json();

        translations.value = data;
        locale.value = lang;

        // If not already in locales list, add it
        if (!locales.value.find((l) => l.id === lang)) {
          locales.value.push({
            id: lang,
            name: lang.toUpperCase(),
            translations: data,
          });
        }

        localStorage.setItem("locale", lang);
      } catch (e) {
        // The dictionary didn't arrive: offline, or a deploy replaced its
        // hashed URL. "App updated" guessed at the second and was English in
        // every locale. Say it in the language still loaded (the one being
        // switched away from), and fall back to English only when none is.
        alert(
          translations.value[LOAD_FAILED_KEY] ??
            "This language couldn’t be loaded. Check your connection, then refresh the page.",
        );
      }
      loaded.value = true;
    };

    const setLocale = async (lang: string) => {
      loaded.value = false;
      await loadLocale(lang);
    };

    const reportedMissingKeys = new Set<string>();

    const t = (key: string, params?: (string | number)[]) => {
      let translation = translations.value[key];

      if (translation === undefined || translation === null) {
        if (loaded.value && key) {
          const dedupeKey = `${locale.value || "default"}:${key}`;
          if (!reportedMissingKeys.has(dedupeKey)) {
            reportedMissingKeys.add(dedupeKey);
            console.error(
              `[Localization] Missing translation key: "${key}" (locale: "${locale.value}")`,
            );
          }
        }
        translation = key;
      }

      if (params && params.length > 0) {
        params.forEach((param, index) => {
          translation = translation.replace(`{${index}}`, String(param));
        });
      }

      return translation;
    };

    const fetchData = async () => {
      if (props.initialLocale && props.files[props.initialLocale]) {
        await loadLocale(props.initialLocale);
        return;
      }

      const saved = localStorage.getItem("locale");
      let fallback = new Intl.Locale(navigator.language || "en-GB").language;
      if (!supportedLanguages.find((el) => el.id === fallback)) fallback = "en";
      const defaultLocale = saved || fallback;
      await loadLocale(defaultLocale);
    };

    onMounted(() => {
      if (loaded.value) return;
      fetchData().then(() => {});
    });
    provide(LocalizationContextKey, {
      t,
      locale,
      translations,
      locales: locales.value,
      setLocale,
    });

    return () => (locale.value ? slots.default?.() : null);
  },
});

// Hook to use Localization
export function useLocalization(): LocalizationContextType {
  const context = inject<LocalizationContextType>(LocalizationContextKey);
  if (!context) {
    throw new Error(
      "useLocalization must be used within an LocalizationProvider",
    );
  }
  return context;
}
